/**
 * The `window.spectreeToolbox` bridge (epic PI-SPEC-SW-E279).
 *
 * SpecTree Toolbox opens SpecTree's pages in its window and exposes this
 * object to them through its preload script. In a plain browser it is
 * absent. It is grouped per toolset (`spectreeToolbox.grooming`); a toolset
 * the app does not ship is absent, and `app.contracts` carries the contract
 * number of each toolset it does ship (see `contracts.ts`).
 *
 * Long-running work (a batch run) reports through events: subscribe with
 * `grooming.onEvent`. Events cross the app/page version boundary, so pages
 * parse them with {@link groomingRunnerEventSchema}.
 *
 * Nothing here carries a credential. The app never hands a Claude, ADO or
 * Git credential to a page.
 *
 * Contract 2 (epic PI-SPEC-SW-E280) adds the `item-progress` event,
 * `app.titleBar`, `app.notify` and the drafted questions on
 * `draftAuthorQuestions`. Every addition is optional, so a page detects a
 * contract-1 app by its absence (or by `app.contracts.grooming`) and falls
 * back; see the package README.
 *
 * Formulator contract 1 (epic PI-SPEC-SW-E282) adds
 * `spectreeToolbox.formulator`: start an interview, send a reply, resume a
 * session that awaits Claude, and stop a turn. Turns run on the person's
 * Claude Code seat and report through `formulator.onEvent`; pages parse the
 * events with {@link formulatorEventSchema}. An app without the formulator
 * toolset has no `formulator` member and no `app.contracts.formulator`.
 *
 * Formulator contract 3 (epic PI-SPEC-SW-E321) adds the optional
 * `formulator.redraft`: rewrite the session's Claude draft following the
 * person's instruction. An older app has no `redraft`, so a page offers
 * Redraft only when `typeof bridge.formulator?.redraft === "function"`.
 *
 * Grooming contract 3 (epic PI-SPEC-SW-E284) adds the run queue: the
 * optional, flat queue methods on `spectreeToolbox.grooming` (`getQueue`,
 * `enqueueItem`, ..., `setQueueConcurrency`) and the `queue-changed` event.
 * A page uses the queue only when `typeof bridge.grooming?.getQueue ===
 * "function"` and `bridge.app.contracts.grooming >= 3`; otherwise it keeps
 * the contract-2 behaviour. In a contract-3 app `groomOne`, `runBatch`,
 * `draftProductAreas` and `draftAuthorQuestions` add queue entries too.
 *
 * Getting started (epic PI-SPEC-SW-E288) adds optional `app` members within
 * grooming contract 3, so no contract number moves: the ordered Setup report
 * (`getSetup`, `runSetupStep`, `onSetupEvent`, see `setup.ts`) and the Claude
 * sign-in result (`signInToClaude`), the unsigned-pilot update notice
 * (`getUpdateNotice`, `onUpdateNotice`, see `updates.ts`) and the bundled MCP
 * server version (`bundledMcpVersion`). Pages feature-detect each method with
 * `typeof bridge.app.getSetup === "function"` (and so on) and fall back to
 * `checkEnvironment()` / `openClaudeSignIn()` / no band when it is absent.
 *
 * The run queue's plain words (epic PI-SPEC-SW-E290, PKG-PM-B) add more
 * optional members within grooming contract 3: `app.signOut` (Sign Out that
 * keeps the queue) and optional fields on the queue state, pause and entry
 * (`grooming/queue.ts`). Pages feature-detect them (`typeof
 * bridge.app.signOut === "function"`, a field present or not) and fall back
 * to the app's own Sign Out and the contract-3 queue view.
 *
 * The Windows title bar (SpecTree Windows walk 1, gap G-02, epic
 * PI-SPEC-SW-E304) adds two more optional `app` members within grooming
 * contract 3, so no contract number moves: `app.reload` and `app.quit`. On
 * Windows the frameless window draws no menu bar, so the page's title-bar
 * account menu offers Reload and Quit next to Sign out of SpecTree. Pages
 * feature-detect each (`typeof bridge.app.reload === "function"`) and hide the
 * item when it is absent.
 *
 * Help contract 1 (epic PI-SPEC-SW-E302, ER-1655) adds
 * `spectreeToolbox.help`: ask a question about the Toolbox (with up to three
 * screenshots and the page context), stop it, end the conversation, read
 * the engine status and the bundled help pack. Turns run read-only on the
 * person's Claude Code seat; answer text streams as `answer-delta` events and
 * pages parse every event with {@link helpEventSchema}. An app without the
 * help toolset has no `help` member and no `app.contracts.help`.
 *
 * FogBugz contract 1 (epic PI-SPEC-SW-E307, ER-1663) adds
 * `spectreeToolbox.fogbugz`: `summarizeEvtx` has the person's own Claude Code
 * write the four summary sections from the deterministic EVTX digest SpecTree
 * returned for an attachment on an existing case, and `cancel` stops it. No
 * events: the call resolves with the sections or an engine error code. The
 * page shows the summary composed by `composeToolboxEvtxSummary` and posts the
 * reviewed sections to SpecTree, never through the bridge. An app without the
 * fogbugz toolset has no `fogbugz` member and no `app.contracts.fogbugz`.
 *
 * Within fogbugz 1 (unpublished, feature PI-SPEC-SW-1602), "Look into the
 * code": `listCodeRefs`, `investigateCode` and `askCode` have the person's own
 * Claude Code study the product code behind a reviewed summary, read-only, at
 * the branch or tag the person picks, and answer up to ten follow-up
 * questions. Progress arrives through `onCodeProgress`
 * (`fogbugzCodeProgressEventSchema`); `cancel` stops a code run too;
 * `closeCodeThread` ends a thread as soon as the page moves on. The
 * report stays in the app: it is never posted to the case.
 * `summarizeEvtx` and `cancel` keep their shapes.
 *
 * FogBugz Formulator contract 1 (epic PI-SPEC-SW-E309, ER-1665 Package C)
 * adds `spectreeToolbox.fogbugzFormulator`: run the interview turns of a
 * session SpecTree created (`start`, `answer`) on the person's own Claude
 * Code seat with the linked ADO project's code open, keep screenshots and
 * `.evtx` files in the session's private folder until submit (`addImage`,
 * `addEventLog`, `listAttachments`, `readAttachment`), summarize an event log
 * with the existing EVTX summary skill (`summarizeEventLog`), and stop or
 * forget a session (`cancel`, `forget`). Turns report through `onEvent`;
 * pages parse every event with {@link fogbugzFormulatorEventSchema}. Review
 * and submit go straight to SpecTree, never through the bridge. An app
 * without the toolset has no `fogbugzFormulator` member and no
 * `app.contracts["fogbugz-formulator"]`; pages feature-detect both.
 *
 * Ask contract 1 (epic PI-SPEC-SW-E310, ER-1667 Package F) adds
 * `spectreeToolbox.ask` ("Ask about the product"): ask a question about one
 * product (an ADO project, or a FogBugz project linked to one) with up to
 * three screenshots, answered by the person's own Claude Code from the
 * product's repository copies and written specs. `checkProduct` says whether
 * the product's code is ready on this computer; `cancel` stops a turn and
 * `closeConversation` deletes its local folder. Answer text streams as
 * `answer-delta` events; pages parse every event with `askEventSchema`. An
 * app without the ask toolset has no `ask` member and no
 * `app.contracts.ask`.
 *
 * FogBugz-to-ADO contract 1 (epic PI-SPEC-SW-E311, ER-1666 Package E) adds
 * `spectreeToolbox.fogbugzToAdo` for Project Managers and Admins
 * (capability `fogbugz-to-ado`): `checkRepos` says whether the linked ADO
 * project's repository copies are on this computer, `readSlots` has the
 * PM's own Claude Code read a FogBugz case (with every case image and the
 * code open) and return a verdict per bug slot plus reporter questions,
 * `draftBug` drafts the ADO Bug from the confirmed slots, and `cancel` stops
 * the run. Progress arrives through the optional `onProgress`
 * (`fogbugzToAdoProgressEventSchema`). Claude never writes: the page shows
 * the Bug composed by `composeFogbugzToAdoBug`, and SpecTree creates it and
 * posts the FogBugz note, never through the bridge. An app without the
 * toolset has no `fogbugzToAdo` member and no
 * `app.contracts["fogbugz-to-ado"]`; pages feature-detect both.
 */
import { z } from "zod";
import { FORMULATOR_DESCRIPTION_MAX, FORMULATOR_RUN_PHASES } from "./formulator/constants.js";
import { formulatorItemIdSchema } from "./formulator/api.js";
import { FORMULATOR_ENGINE_ERROR_CODES } from "./formulator/errors.js";
import { FORMULATOR_KINDS, FORMULATOR_TURN_OUTCOMES } from "./formulator/types.js";
import { groomingProjectNameSchema } from "./grooming/api.js";
import { HELP_ANSWER_MAX, HELP_RUN_PHASES, HELP_TURN_OUTCOMES, HELP_VERDICTS } from "./help/constants.js";
import { HELP_ENGINE_ERROR_CODES } from "./help/errors.js";
import { askHandoffSchema } from "./ask/handoff.js";
import { fogbugzLinkRepoRefSchema } from "./fogbugz/link.js";
import { FOGBUGZ_EVTX_SECTIONS_MAX_CHARS } from "./fogbugz/constants.js";
import { FOGBUGZ_FORMULATOR_AWAITING, FOGBUGZ_FORMULATOR_REPLY_MAX, FOGBUGZ_FORMULATOR_TURN_MODES, } from "./fogbugz-formulator/constants.js";
import { FOGBUGZ_FORMULATOR_ENGINE_ERROR_CODES, } from "./fogbugz-formulator/errors.js";
import { fogbugzFormulatorAttachmentNameSchema, fogbugzFormulatorSessionIdSchema, } from "./fogbugz-formulator/schemas.js";
import { helpActionSchema, helpLinkSchema, helpSourceSchema, helpStatusSchema, } from "./help/types.js";
import { GROOMING_ENGINE_ERROR_CODES } from "./grooming/errors.js";
import { GROOMING_ACTIVITY_MAX_CHARS, MAX_GROOMING_REPOS } from "./grooming/constants.js";
import { GROOMING_RUN_PHASES, GROOMING_STOP_REASONS } from "./grooming/types.js";
import { groomingQueueStateSchema, } from "./grooming/queue.js";
// ---------------------------------------------------------------------------
// App chrome (contract 2)
// ---------------------------------------------------------------------------
/**
 * How the app draws its window frame: `mac-inset` (macOS traffic lights over
 * the page), `windows-overlay` (Windows min/max/close drawn over the page's
 * top-right corner) or `native` (an ordinary OS title bar; the page reserves
 * nothing).
 */
export const TOOLBOX_TITLE_BAR_STYLES = ["mac-inset", "windows-overlay", "native"];
/** The operating system the app runs on, as Node's `process.platform` names it. */
export const TOOLBOX_PLATFORMS = ["darwin", "win32", "linux"];
/**
 * Contract 2. The window frame the page draws its own title bar inside. The
 * page keeps `reservedLeft` / `reservedRight` pixels free for the native
 * window controls and makes its title bar `height` pixels tall. Contract-1
 * apps omit it; the page then draws no desktop title bar.
 */
export const toolboxTitleBarSchema = z.object({
    style: z.enum(TOOLBOX_TITLE_BAR_STYLES),
    platform: z.enum(TOOLBOX_PLATFORMS),
    reservedLeft: z.number().min(0),
    reservedRight: z.number().min(0),
    height: z.number().min(0),
});
/**
 * Contract 2. An OS notification the page asks the app to show, for example
 * "Briefs ready" when a batch finishes. `targetPath` is a SpecTree path
 * (starting with `/`) the app opens when the person clicks the notification.
 * Contract-1 apps have no `notify`; the page shows an in-page toast instead.
 */
export const toolboxNotificationSchema = z.object({
    title: z.string().min(1).max(80),
    body: z.string().max(240),
    targetPath: z.string().startsWith("/").max(512).optional(),
});
/**
 * What `ToolboxBridge.app.signOut()` resolves to (epic PI-SPEC-SW-E290).
 * `message` is one plain sentence for the person when the sign-out failed.
 * Lenient, so a newer app may add fields.
 */
export const toolboxSignOutResultSchema = z.discriminatedUnion("ok", [
    z.object({ ok: z.literal(true) }).loose(),
    z.object({ ok: z.literal(false), message: z.string() }).loose(),
]);
// ---------------------------------------------------------------------------
// Repository copies
// ---------------------------------------------------------------------------
/**
 * State of the app's local copy of one mapped repository:
 * `missing` (not cloned yet), `cloning`, `fetching`, `ready`,
 * `auth-required` (Git needs a sign-in; see `signInToRepos`) or `error`.
 */
export const GROOMING_REPO_STATES = ["missing", "cloning", "fetching", "ready", "auth-required", "error"];
export const groomingRepoStatusSchema = z.object({
    /** ADO project that owns the repository, when it is not the configured project. */
    project: z.string().optional(),
    repo: z.string(),
    branch: z.string().optional(),
    state: z.enum(GROOMING_REPO_STATES),
    /** When the copy was last fetched, ISO 8601; null when never. */
    lastFetchedAt: z.string().nullable(),
    /** One plain sentence when `state` is `auth-required` or `error`. */
    message: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------
const engineErrorCodeSchema = z.enum(GROOMING_ENGINE_ERROR_CODES);
/** The engine started briefing one item. */
export const groomingItemStartedEventSchema = z.object({
    type: z.literal("item-started"),
    batchId: z.string(),
    batchItemId: z.string(),
    itemId: z.string(),
});
/**
 * Contract 2. The engine moved to a new phase of one item's run, or reports a
 * new plain activity line within it. One line replaces the previous one; the
 * page keeps no history. `startedAt` is the run's start (ISO 8601) so the page
 * can show elapsed time. A one-item run (`groomOne`) may report before the
 * batch item exists, so `batchItemId` and `itemId` are optional. Contract-1
 * apps never send this event; pages fall back to a spinner.
 */
export const groomingItemProgressEventSchema = z.object({
    type: z.literal("item-progress"),
    batchId: z.string(),
    batchItemId: z.string().optional(),
    itemId: z.string().optional(),
    phase: z.enum(GROOMING_RUN_PHASES),
    /** One plain phrase, for example "Reading the item". Never a raw tool name, path or prompt. */
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
    /** When the run started, ISO 8601. */
    startedAt: z.string(),
});
/** The engine finished one item: briefed, below the bar, or failed. */
export const groomingItemFinishedEventSchema = z.object({
    type: z.literal("item-finished"),
    batchId: z.string(),
    batchItemId: z.string(),
    itemId: z.string(),
    outcome: z.enum(["briefed", "not-ready", "failed"]),
    errorCode: engineErrorCodeSchema.optional(),
    message: z.string().optional(),
});
/** The batch stopped before its last item; the rest stay queued. */
export const groomingBatchStoppedEventSchema = z.object({
    type: z.literal("batch-stopped"),
    batchId: z.string(),
    reason: z.enum(GROOMING_STOP_REASONS),
    errorCode: engineErrorCodeSchema.optional(),
    message: z.string().optional(),
    /** For `usage-limit`: when the limit resets, ISO 8601. */
    resumeAt: z.string().optional(),
});
/** The batch ran every item, or the person canceled it. */
export const groomingBatchFinishedEventSchema = z.object({
    type: z.literal("batch-finished"),
    batchId: z.string(),
    status: z.enum(["completed", "canceled"]),
    briefed: z.number().int().min(0),
    notReady: z.number().int().min(0),
    failed: z.number().int().min(0),
});
/** The repository copies of a project changed state. */
export const groomingRepoStatusEventSchema = z.object({
    type: z.literal("repo-status"),
    project: z.string(),
    repos: z.array(groomingRepoStatusSchema),
});
/**
 * Contract 3. The run queue changed: an entry was added, started, finished,
 * moved or removed, the queue paused or resumed, the concurrency changed or a
 * new Claude usage reading arrived. Carries the whole queue state; the page
 * replaces what it holds. Contract-2 apps never send this event.
 */
export const groomingQueueChangedEventSchema = z.object({
    type: z.literal("queue-changed"),
    queue: groomingQueueStateSchema,
});
/**
 * Every event `grooming.onEvent` delivers. Pages parse with `safeParse` and
 * ignore events that do not parse: a newer app may send event types this
 * contract does not know.
 */
export const groomingRunnerEventSchema = z.discriminatedUnion("type", [
    groomingItemStartedEventSchema,
    groomingItemProgressEventSchema,
    groomingItemFinishedEventSchema,
    groomingBatchStoppedEventSchema,
    groomingBatchFinishedEventSchema,
    groomingRepoStatusEventSchema,
    groomingQueueChangedEventSchema,
]);
// ---------------------------------------------------------------------------
// Formulator toolset (contract formulator 1)
// ---------------------------------------------------------------------------
/**
 * What `formulator.start` takes: a new item (`project`, `kind` and the
 * person's own `description`) or an existing item to improve (`project` and
 * `itemId`). The app checks the project's mapped repositories before it
 * creates the session.
 */
export const formulatorStartInputSchema = z.union([
    z
        .object({
        project: groomingProjectNameSchema,
        kind: z.enum(FORMULATOR_KINDS),
        description: z
            .string()
            .trim()
            .min(1, "description must not be empty")
            .max(FORMULATOR_DESCRIPTION_MAX),
    })
        .strict(),
    z.object({ project: groomingProjectNameSchema, itemId: formulatorItemIdSchema }).strict(),
]);
const formulatorErrorCodeSchema = z.enum(FORMULATOR_ENGINE_ERROR_CODES);
/** SpecTree created the session; the opening turn follows. */
export const formulatorSessionStartedEventSchema = z.object({
    type: z.literal("session-started"),
    sessionId: z.string(),
});
/**
 * The engine moved to a new phase of a turn, or reports a new plain activity
 * line within it. One line replaces the previous one. `sessionId` is absent
 * while the repositories are prepared before the session exists. `startedAt`
 * is the turn's start (ISO 8601).
 */
export const formulatorTurnProgressEventSchema = z.object({
    type: z.literal("turn-progress"),
    sessionId: z.string().optional(),
    phase: z.enum(FORMULATOR_RUN_PHASES),
    /** One plain phrase, for example "Checking the code". Never a raw tool name, path or prompt. */
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
    /** When the turn started, ISO 8601. */
    startedAt: z.string(),
});
/**
 * A turn ended: Claude `asked` the next question, the draft is `complete`, or
 * the turn `failed` (nothing was posted; the page keeps the typed reply).
 */
export const formulatorTurnFinishedEventSchema = z.object({
    type: z.literal("turn-finished"),
    sessionId: z.string(),
    outcome: z.enum(FORMULATOR_TURN_OUTCOMES),
    errorCode: formulatorErrorCodeSchema.optional(),
    message: z.string().optional(),
});
/** The repository copies of a project changed state (same shape as Grooming's). */
export const formulatorRepoStatusEventSchema = z.object({
    type: z.literal("repo-status"),
    project: z.string(),
    repos: z.array(groomingRepoStatusSchema),
});
/**
 * Every event `formulator.onEvent` delivers. Pages parse with `safeParse` and
 * ignore events that do not parse: a newer app may send event types this
 * contract does not know.
 */
export const formulatorEventSchema = z.discriminatedUnion("type", [
    formulatorSessionStartedEventSchema,
    formulatorTurnProgressEventSchema,
    formulatorTurnFinishedEventSchema,
    formulatorRepoStatusEventSchema,
]);
const helpErrorCodeSchema = z.enum(HELP_ENGINE_ERROR_CODES);
/** The engine's readiness changed (signed in, usage limit reached or reset, ...). */
export const helpStatusChangedEventSchema = z.object({
    type: z.literal("status-changed"),
    status: helpStatusSchema,
});
/** The engine started a turn for a question. */
export const helpTurnStartedEventSchema = z.object({
    type: z.literal("turn-started"),
    conversationId: z.string(),
    turnId: z.string(),
});
/**
 * More answer text, in order. The page appends `text` to the turn's answer.
 * The marker line is never in it (the engine strips it).
 */
export const helpAnswerDeltaEventSchema = z.object({
    type: z.literal("answer-delta"),
    conversationId: z.string(),
    turnId: z.string(),
    text: z.string(),
});
/**
 * The turn moved to a new phase. The page shows the phase's plain phrase
 * (`HELP_RUN_PHASE_LABELS`) until answer text arrives.
 */
export const helpTurnProgressEventSchema = z.object({
    type: z.literal("turn-progress"),
    conversationId: z.string(),
    turnId: z.string(),
    phase: z.enum(HELP_RUN_PHASES),
    /** One plain phrase, for example "Reading the guide". Never a raw tool name, path or prompt. */
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
});
/**
 * A turn ended. `answered`: `answer` is the whole answer text (marker
 * stripped) with the verdict, sources, in-app links and confirm buttons the
 * engine kept after filtering. `failed`: `errorCode` and `message` say why;
 * the page keeps the typed question. `stopped`: the person stopped it.
 */
export const helpTurnFinishedEventSchema = z.object({
    type: z.literal("turn-finished"),
    conversationId: z.string(),
    turnId: z.string(),
    outcome: z.enum(HELP_TURN_OUTCOMES),
    answer: z.string().max(HELP_ANSWER_MAX).optional(),
    verdict: z.enum(HELP_VERDICTS).optional(),
    sources: z.array(helpSourceSchema).default([]),
    links: z.array(helpLinkSchema).default([]),
    actions: z.array(helpActionSchema).default([]),
    errorCode: helpErrorCodeSchema.optional(),
    message: z.string().optional(),
});
/**
 * Every event `help.onEvent` delivers. Pages parse with `safeParse` and
 * ignore events that do not parse: a newer app may send event types this
 * contract does not know.
 */
export const helpEventSchema = z.discriminatedUnion("type", [
    helpStatusChangedEventSchema,
    helpTurnStartedEventSchema,
    helpAnswerDeltaEventSchema,
    helpTurnProgressEventSchema,
    helpTurnFinishedEventSchema,
]);
// ---------------------------------------------------------------------------
// FogBugz Formulator toolset (contract fogbugz-formulator 1)
// ---------------------------------------------------------------------------
/**
 * What `fogbugzFormulator.start` takes: a session SpecTree already created
 * (from the routing screen), the ADO project linked to its FogBugz project,
 * and that project's mapped repositories (both from the session view). The
 * engine checks the repository copies on this computer (`REPO_NOT_SET_UP`
 * when they are missing) and runs the opening turn.
 *
 * `priorFindings` (optional, feature PI-SPEC-SW-1631): the Product Q&A
 * hand-off the page was opened with (`askHandoffSchema`: question, plain
 * answer, code findings). The engine copies it into the opening turn's
 * `turn.json` as untrusted context, and never sends it to SpecTree; the
 * skill confirms every slot with the person.
 */
export const fogbugzFormulatorStartInputSchema = z
    .object({
    sessionId: fogbugzFormulatorSessionIdSchema,
    adoProject: groomingProjectNameSchema,
    repos: z.array(fogbugzLinkRepoRefSchema).min(1).max(MAX_GROOMING_REPOS),
    priorFindings: askHandoffSchema.optional(),
})
    .strict();
/** What `fogbugzFormulator.answer` takes: the person's answer to the pending question. */
export const fogbugzFormulatorAnswerInputSchema = z
    .object({
    sessionId: fogbugzFormulatorSessionIdSchema,
    answer: z
        .string()
        .min(1)
        .max(FOGBUGZ_FORMULATOR_REPLY_MAX)
        .refine((value) => value.trim().length > 0, "answer must not be blank"),
})
    .strict();
/** A session and one of its attachment file names. */
export const fogbugzFormulatorAttachmentRefSchema = z
    .object({
    sessionId: fogbugzFormulatorSessionIdSchema,
    name: fogbugzFormulatorAttachmentNameSchema,
})
    .strict();
/** The kinds of file a session keeps until submit. */
export const FOGBUGZ_FORMULATOR_ATTACHMENT_KINDS = ["image", "event-log"];
/** One kept file, as `listAttachments` reports it (no bytes). */
export const fogbugzFormulatorAttachmentInfoSchema = z.object({
    name: fogbugzFormulatorAttachmentNameSchema,
    kind: z.enum(FOGBUGZ_FORMULATOR_ATTACHMENT_KINDS),
    sizeBytes: z.number().int().min(0),
});
const fogbugzFormulatorErrorCodeSchema = z.enum(FOGBUGZ_FORMULATOR_ENGINE_ERROR_CODES);
/** The engine started a turn for a session. `startedAt` is ISO 8601. */
export const fogbugzFormulatorTurnStartedEventSchema = z.object({
    type: z.literal("turn-started"),
    sessionId: z.string(),
    mode: z.enum(FOGBUGZ_FORMULATOR_TURN_MODES),
    startedAt: z.string(),
});
/**
 * A turn ended and SpecTree accepted it. `awaiting` is the session's state
 * after the turn: `person` (a question waits) or `review` (coverage is
 * complete). The page reads the session back from SpecTree.
 */
export const fogbugzFormulatorTurnFinishedEventSchema = z.object({
    type: z.literal("turn-finished"),
    sessionId: z.string(),
    awaiting: z.enum(FOGBUGZ_FORMULATOR_AWAITING),
});
/** A turn failed: nothing was posted and the page keeps the typed answer. */
export const fogbugzFormulatorTurnFailedEventSchema = z.object({
    type: z.literal("turn-failed"),
    sessionId: z.string(),
    code: fogbugzFormulatorErrorCodeSchema,
    message: z.string(),
});
/**
 * An event-log summary finished (also the value `summarizeEventLog`
 * resolves with), so every open page showing the session can offer it for
 * review. Sections are untrusted model output: check with
 * `hasAllEvtxSections` before showing.
 */
export const fogbugzFormulatorSummaryReadyEventSchema = z.object({
    type: z.literal("summary-ready"),
    sessionId: z.string(),
    name: fogbugzFormulatorAttachmentNameSchema,
    sections: z.string().min(1).max(FOGBUGZ_EVTX_SECTIONS_MAX_CHARS),
});
/**
 * Every event `fogbugzFormulator.onEvent` delivers. Pages parse with
 * `safeParse` and ignore events that do not parse: a newer app may send
 * event types this contract does not know.
 */
export const fogbugzFormulatorEventSchema = z.discriminatedUnion("type", [
    fogbugzFormulatorTurnStartedEventSchema,
    fogbugzFormulatorTurnFinishedEventSchema,
    fogbugzFormulatorTurnFailedEventSchema,
    fogbugzFormulatorSummaryReadyEventSchema,
]);
//# sourceMappingURL=bridge.js.map